import React, { Fragment, useState } from "react";
import banner from "../../../assets/images/about.jpeg";
import choice from "../../../assets/images/choose.webp";
import Header from "./header";
import Footer from "./footer";
import "./venue-form.css";

import {
  withRouter,
  Row,
  Col,
  toast,
  Axios,
  SELECT,
  TEXTFIELD,
  SUBMIT,
  RESET,
  ApiUrl,
  Loading,
} from "../../../components";

const { VENUE_BOOKING_HOME } = ApiUrl;

const VenueBookingHome = () => {

  const [formData, setFormData] = useState({
    case_name: "",
    case_number: "",
    parties_involved: "",
    preferred_venue: "",
    preferred_seat: "",
    booking_date: "",
    start_time: "",
    end_time: "",
    stenography: false,
    transcription: false,
    pantry_service: false,
    vide_conference: false,
    contact_full_name: "",
    contact_organization: "",
    contact_email: "",
    alternate_email: "",
    contact_phone: "",
    alternate_phone: "",
    preferred_communication_whatsapp: false,
    preferred_communication_email: false,
    preferred_communication_sms: false,
  });

  const [errors, setErrors] = useState({});
  const [focusedField, setFocusedField] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const requiredFields = [
    "case_name",
    "parties_involved",
    "preferred_venue",
    "preferred_seat",
    "booking_date",
    "start_time",
    "end_time",
    "contact_full_name",
    "contact_organization",
    "contact_email",
    "contact_phone",
  ];

  const validateField = (name, value) => {
    let error = "";

    switch (name) {
      case "case_name":
        if (value && !/^[a-zA-Z0-9\s]*$/.test(value)) {
          error = "Only letters, numbers, and spaces are allowed";
        } else if (!value.trim()) {
          error = "Required";
        }
        break;
      case "parties_involved":
      case "contact_full_name":
      case "contact_organization":
        if (value && !/^[a-zA-Z\s]*$/.test(value)) {
          error = "Only letters and spaces are allowed";
        } else if (!value.trim()) {
          error = "Required";
        }
        break;
      case "contact_email":
      case "alternate_email":
        if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          error = "Invalid email format";
        } else if (name === "contact_email" && !value.trim()) {
          error = "Required";
        }
        break;
      case "contact_phone":
      case "alternate_phone":
        if (value && !/^\d{10}$/.test(value)) {
          error = "Phone number must be 10 digits";
        } else if (name === "contact_phone" && !value.trim()) {
          error = "Required";
        }
        break;
      case "preferred_venue":
      case "preferred_seat":
      case "booking_date":
      case "start_time":
      case "end_time":
        if (!value.trim()) {
          error = "Required";
        }
        break;
      default:
        break;
    }
    return error;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "case_name") {
      if (value && !/^[a-zA-Z0-9\s]*$/.test(value)) {
        return;
      }
    } else if (["parties_involved", "contact_full_name", "contact_organization"].includes(name)) {
      if (value && !/^[a-zA-Z\s]*$/.test(value)) {
        return;
      }
    }

    if (["contact_phone", "alternate_phone"].includes(name)) {
      if (value && !/^\d*$/.test(value)) {
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    const error = validateField(name, type === "checkbox" ? checked : value);
    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  const handleFocus = (e) => {
    const { name } = e.target;
    setFocusedField(name);
    if (requiredFields.includes(name) && !formData[name].trim()) {
      setErrors((prev) => ({
        ...prev,
        [name]: "Required",
      }));
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setFocusedField(null);
    const error = validateField(name, formData[name]);
    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    const newErrors = {};
    Object.keys(formData).forEach((key) => {
      const error = validateField(key, formData[key]);
      if (error) newErrors[key] = error;
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      const errorFields = Object.keys(newErrors)
        .map(field =>
          field
            .replace(/_/g, ' ')
            .replace(/\b\w/g, char => char.toUpperCase())
        )
        .join(', ');
      toast.error(`${errorFields} are required`);
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await Axios.post(VENUE_BOOKING_HOME, formData);
      toast.success("Venue booking submitted successfully!");
      setFormData({
        case_name: "",
        case_number: "",
        parties_involved: "",
        preferred_venue: "",
        preferred_seat: "",
        booking_date: "",
        start_time: "",
        end_time: "",
        stenography: false,
        transcription: false,
        pantry_service: false,
        vide_conference: false,
        contact_full_name: "",
        contact_organization: "",
        contact_email: "",
        alternate_email: "",
        contact_phone: "",
        alternate_phone: "",
        preferred_communication_whatsapp: false,
        preferred_communication_email: false,
        preferred_communication_sms: false,
      });
      setErrors({});
    } catch (error) {
      console.error("Error occurred while submitting data:", error);
      toast.error("Failed to submit venue booking. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Header />
      <Fragment>

        {isSubmitting && (
          <div className="full-page-loader">
            <div className="loader-content">
              <Loading />
            </div>
          </div>
        )}

        <div className="container">
          <div className="venue-booking-form py-5">
            <h2 className="mb-4 font-weight-bold">Venue booking</h2>

            {/* Arbitration form details */}
            <div className="bg-white rounded-10">
              <h4 className="px-3 pt-3 pb-2">Arbitration details</h4>
              <div className="venue-heading-underline"></div>
              <form>
                <div className="row m-0 pt-4">
                  <div className="form-group col-md-6">
                    <label>Case Name</label>
                    <input
                      type="text"
                      name="case_name"
                      className="venue-field-input"
                      placeholder="Enter case name"
                      value={formData.case_name}
                      onChange={handleChange}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                    />
                    {errors.case_name && <span className="text-danger">{errors.case_name}</span>}
                  </div>
                  <div className="form-group col-md-6">
                    <label>Case no. <span className="theme-clr">(Optional)</span></label>
                    <input
                      type="text"
                      name="case_number"
                      className="venue-field-input"
                      placeholder="Enter case number"
                      value={formData.case_number}
                      onChange={handleChange}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                    />
                    {errors.case_number && <span className="text-danger">{errors.case_number}</span>}
                  </div>
                  <div className="form-group col-md-12">
                    <label>Parties involved</label>
                    <input
                      type="text"
                      name="parties_involved"
                      className="venue-field-input"
                      placeholder="Enter parties involved"
                      value={formData.parties_involved}
                      onChange={handleChange}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                    />
                    {errors.parties_involved && <span className="text-danger">{errors.parties_involved}</span>}
                  </div>
                </div>
              </form>
            </div>

            {/* Booking form detail */}
            <div className="bg-white rounded-10 mt-4">
              <h4 className="px-3 pt-3 pb-2">Booking details</h4>
              <div className="venue-heading-underline"></div>
              <form>
                <div className="row m-0 pt-4">
                  <div className="form-group col-md-6">
                    <label>Preferred Venue</label>
                    <select
                      name="preferred_venue"
                      className="venue-field-input"
                      value={formData.preferred_venue}
                      onChange={handleChange}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                    >
                      <option value="">Select a venue</option>
                      <option value="preferred venue One">preferred venue One</option>
                      <option value="preferred venue Two">preferred venue Two</option>
                      <option value="preferred venue Three">preferred venue Three</option>
                    </select>
                    {errors.preferred_venue && <span className="text-danger">{errors.preferred_venue}</span>}
                  </div>
                  <div className="form-group col-md-6">
                    <label>Preferred Seat</label>
                    <select
                      name="preferred_seat"
                      className="venue-field-input"
                      value={formData.preferred_seat}
                      onChange={handleChange}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                    >
                      <option value="">Select a seat</option>
                      <option value="preferred venue One">preferred venue One</option>
                      <option value="preferred venue Two">preferred venue Two</option>
                      <option value="preferred venue Three">preferred venue Three</option>
                    </select>
                    {errors.preferred_seat && <span className="text-danger">{errors.preferred_seat}</span>}
                  </div>
                  <div className="form-group col-md-4">
                    <label>Date</label>
                    <input
                      type="date"
                      name="booking_date"
                      className="venue-field-input"
                      value={formData.booking_date}
                      onChange={handleChange}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                    />
                    {errors.booking_date && <span className="text-danger">{errors.booking_date}</span>}
                  </div>
                  <div className="form-group col-md-4">
                    <label>Start time</label>
                    <input
                      type="time"
                      name="start_time"
                      className="venue-field-input"
                      value={formData.start_time}
                      onChange={handleChange}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                    />
                    {errors.start_time && <span className="text-danger">{errors.start_time}</span>}
                  </div>
                  <div className="form-group col-md-4">
                    <label>End time</label>
                    <input
                      type="time"
                      name="end_time"
                      className="venue-field-input"
                      value={formData.end_time}
                      onChange={handleChange}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                    />
                    {errors.end_time && <span className="text-danger">{errors.end_time}</span>}
                  </div>
                  <div className="col-md-12">
                    <h4 className="font-weight-bold">Required secretarial services</h4>
                    <div className="d-flex align-items-start mb-3">
                      <input
                        type="checkbox"
                        name="stenography"
                        className="checkbox"
                        checked={formData.stenography}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                      />
                      <div>
                        <p className="m-0">Stenography</p>
                        <p className="m-0 grey-clr">
                          Stenography plays a crucial role in the legal field, especially for advocates and court proceedings.
                        </p>
                      </div>
                    </div>
                    <div className="d-flex align-items-start mb-3">
                      <input
                        type="checkbox"
                        name="transcription"
                        className="checkbox"
                        checked={formData.transcription}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                      />
                      <div>
                        <p className="m-0">Transcription</p>
                        <p className="m-0 grey-clr">
                          Transcription in the legal field refers to the process of converting spoken words from court proceedings, client meetings, or recorded interviews into written text.
                        </p>
                      </div>
                    </div>
                    <div className="d-flex align-items-start mb-3">
                      <input
                        type="checkbox"
                        name="pantry_service"
                        className="checkbox"
                        checked={formData.pantry_service}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                      />
                      <div>
                        <p className="m-0">Pantry service</p>
                        <p className="m-0 grey-clr">
                          In law offices and court settings, a well-managed pantry service ensures that advocates, clerks, and clients have access to tea, coffee, water, and light snacks, especially during long meetings or proceedings.
                        </p>
                      </div>
                    </div>
                    <div className="d-flex align-items-start mb-3">
                      <input
                        type="checkbox"
                        name="vide_conference"
                        className="checkbox"
                        checked={formData.vide_conference}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                      />
                      <div>
                        <p className="m-0">Video conferencing (VC)</p>
                        <p className="m-0 grey-clr">
                          With secure platforms and high-quality audio-visual setups, video conferencing ensures that legal proceedings remain professional, confidential, and efficient, even when conducted from a distance.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>

            {/* Contact details of booking party form details */}
            <div className="bg-white rounded-10 mt-4">
              <h4 className="px-3 pt-3 pb-2">Contact details of booking party</h4>
              <div className="venue-heading-underline"></div>
              <form>
                <div className="row m-0 pt-4">
                  <div className="form-group col-md-6">
                    <label>Full Name</label>
                    <input
                      type="text"
                      name="contact_full_name"
                      className="venue-field-input"
                      placeholder="Enter your full name"
                      value={formData.contact_full_name}
                      onChange={handleChange}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                    />
                    {errors.contact_full_name && <span className="text-danger">{errors.contact_full_name}</span>}
                  </div>
                  <div className="form-group col-md-6">
                    <label>Organization</label>
                    <input
                      type="text"
                      name="contact_organization"
                      className="venue-field-input"
                      placeholder="Enter your organization"
                      value={formData.contact_organization}
                      onChange={handleChange}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                    />
                    {errors.contact_organization && <span className="text-danger">{errors.contact_organization}</span>}
                  </div>
                  <div className="form-group col-md-6">
                    <label>Email address</label>
                    <input
                      type="email"
                      name="contact_email"
                      className="venue-field-input"
                      placeholder="Enter your email"
                      value={formData.contact_email}
                      onChange={handleChange}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                    />
                    {errors.contact_email && <span className="text-danger">{errors.contact_email}</span>}
                  </div>
                  <div className="form-group col-md-6">
                    <label>Alternative Email Address</label>
                    <input
                      type="email"
                      name="alternate_email"
                      className="venue-field-input"
                      placeholder="Enter alternative email"
                      value={formData.alternate_email}
                      onChange={handleChange}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                    />
                    {errors.alternate_email && <span className="text-danger">{errors.alternate_email}</span>}
                  </div>
                  <div className="form-group col-md-6">
                    <label>Phone Number</label>
                    <input
                      type="text"
                      name="contact_phone"
                      className="venue-field-input"
                      placeholder="Enter your phone number"
                      value={formData.contact_phone}
                      onChange={handleChange}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                    />
                    {errors.contact_phone && <span className="text-danger">{errors.contact_phone}</span>}
                  </div>
                  <div className="form-group col-md-6">
                    <label>Alternative Phone number</label>
                    <input
                      type="text"
                      name="alternate_phone"
                      className="venue-field-input"
                      placeholder="Enter alternative phone number"
                      value={formData.alternate_phone}
                      onChange={handleChange}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                    />
                    {errors.alternate_phone && <span className="text-danger">{errors.alternate_phone}</span>}
                  </div>
                  <div className="col-md-12">
                    <p className="m-0">Preferred Mode of communication</p>
                    <div>
                      <input
                        type="checkbox"
                        name="preferred_communication_whatsapp"
                        className="checkbox"
                        checked={formData.preferred_communication_whatsapp}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                      />
                      <label>WhatsApp</label>
                    </div>
                    <div>
                      <input
                        type="checkbox"
                        name="preferred_communication_email"
                        className="checkbox"
                        checked={formData.preferred_communication_email}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                      />
                      <label>Email</label>
                    </div>
                    <div>
                      <input
                        type="checkbox"
                        name="preferred_communication_sms"
                        className="checkbox"
                        checked={formData.preferred_communication_sms}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                      />
                      <label>SMS</label>
                    </div>
                  </div>
                </div>
              </form>
            </div>

            <div className="d-flex justify-content-center">
              <button
                type="button"
                className="submit-request-form"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? <Loading /> : "Submit Request"}
              </button>
            </div>

          </div>
        </div>
      </Fragment>
      <Footer />
    </>
  );
};

export default withRouter(VenueBookingHome);